import {
    BaseEnvironment,
    type KeycloakContext,
} from "@keycloak/keycloak-ui-shared";
import Keycloak from "keycloak-js";

export async function saveAvatar(
    context: KeycloakContext<BaseEnvironment>,
    avatar: ArrayBuffer | undefined,
): Promise<void> {
    const response = await request("avatar", context, { body: avatar, method: "POST" });
    if (!response.ok) {
        const { errors } = await response.json();
        throw errors;
    }
    return undefined;
}

export async function deleteAvatar(
    context: KeycloakContext<BaseEnvironment>,
): Promise<void> {
    const response = await request("avatar", context, { method: "DELETE" });
    if (!response.ok) {
        const { errors } = await response.json();
        throw errors;
    }
    return undefined;
}

/**
 * Below code is copied from account ui sources as the functions are not exported to be reused here :/
 */

//copied from account ui ./constants
export const CONTENT_TYPE_HEADER = "content-type";
export const CONTENT_TYPE_JSON = "application/json";

//copied from account ui ../utils/joinPath
export function joinPath(...paths: string[]) {
    const normalizedPaths = paths.map((path, index) => {
        const isFirst = index === 0;
        const isLast = index === paths.length - 1;

        // Strip out any leading slashes from the path.
        if (!isFirst && path.startsWith('/')) {
            path = path.slice(1);
        }

        // Strip out any trailing slashes from the path.
        if (!isLast && path.endsWith('/')) {
            path = path.slice(0, -1);
        }

        return path;
    }, []);

    return normalizedPaths.join('/');
}

//copied from account ui request.ts
export type RequestOptions = {
    signal?: AbortSignal;
    getAccessToken?: () => Promise<string | undefined>;
    method?: "POST" | "PUT" | "DELETE";
    searchParams?: Record<string, string>;
    body?: unknown;
};

async function _request(
    url: URL,
    { signal, getAccessToken, method, searchParams, body }: RequestOptions = {},
): Promise<Response> {
    if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) =>
            url.searchParams.set(key, value),
        );
    }

    return fetch(url, {
        signal,
        method,
        body: body ? typeof body === "string" ? body : body instanceof ArrayBuffer ? body : undefined : undefined,
        headers: {
            [CONTENT_TYPE_HEADER]: "application/octet-stream",
            authorization: `Bearer ${await getAccessToken?.()}`,
        },
    });
}

export async function request(
    path: string,
    { environment, keycloak }: KeycloakContext<BaseEnvironment>,
    opts: RequestOptions = {},
    fullUrl?: URL,
) {
    if (typeof fullUrl === "undefined") {
        fullUrl = url(environment, path);
    }
    return _request(fullUrl, {
        ...opts,
        getAccessToken: token(keycloak),
    });
}

export const url = (environment: BaseEnvironment, path: string) =>
    new URL(
        joinPath(
            environment.serverBaseUrl,
            "realms",
            environment.realm,
            // "account",
            path,
        )
    );

export const token = (keycloak: Keycloak) =>
    async function getAccessToken() {
        try {
            await keycloak.updateToken(5);
        } catch {
            await keycloak.login();
        }

        return keycloak.token;
    };